
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import { ClientDiscount, ClientRegistrationDetail } from "@/domain/entities/ClientRegistration";
import ConfirmationApprovalUpdateRates  from "../../../global-components/confirmation-approval-update-rates.vue";
import {
  formatDate,
  formatDateNormal,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import {
  fileExtensionFromUrl,
  fileNameFromUrl
} from "@/app/infrastructures/misc/Utils";
import PriceTypeForm from "../../../global-components/price-type-form.vue";
import { AbortUploadRateApiRequest, EditClientRegistrationApiRequestV2 } from "@/data/payload/api/ClientRegistrationApiRequest";
import { ClientRegistrationController } from "@/app/ui/controllers/ClientRegistrationController";
import { ModalMessageEntities, OptionsClass } from "@/domain/entities/MainApp";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ClientManagementController } from "@/app/ui/controllers/ClientManagementController";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";
import downloadFile from "@/app/infrastructures/misc/common-library/DownloadFile";
import ModalChangeInsurance from "../../../global-components/modal-change-insurance.vue";
import { FlagsPermissionClient } from "@/feature-flags/flags-permission-client";
import { ClientController } from "@/app/ui/controllers/ClientController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import moment from "moment";
import { configDates } from "@/app/infrastructures/misc/common-library/modules/Date";
import { BeneficiaryAccountController } from "@/app/ui/controllers/BeneficiaryAccountController";
import ModalDiskon from "./modal-diskon.vue";
import needsAuth from "@/app/infrastructures/misc/common-library/NeedsAuth";
import WidgetBulkDownloadClient from "./widget-bulk-download-client.vue";

class Props {
  isManagement = prop<boolean>({
    type: Boolean,
    required: false,
    default: false
  });
}

const CLIENT_RATE_ERROR_FILE_EXT = ".xlsx";

@Options({
  components: {
    PriceTypeForm,
    ModalChangeInsurance,
    ConfirmationApprovalUpdateRates,
    ModalDiskon,
    WidgetBulkDownloadClient
  }
})
class InfoDetailClient extends Vue.with(Props) {
  modelDialog = false;
  isUpdateRateConfirmation = false;
  isUpdateInsurance = false;
  updateRateDialog = false;
  updatePrice = false;
  isVisibleModalDiskon = false;
  errorMessage = "";

  form = {
    isCustomRate: "retail",
    isInsurance: false,
    insurance: "",
    customRates: "",
    priceRateStartDate: "",
    priceRateEndDate: "",
    originCities: [] as OptionsClass[],
    discount: "" as any,
    discountProductTypes: "" as any,
    versionName: "",
    archiveFile: "" as any,
    retailBasedDistrict: false,
    versionBasic: "" as any,
    useClientDiscountConfig: true
  };

  error = {
    isCustomRate: false,
    isInsurance: false,
    insurance: false,
    customRates: false,
    priceRateStartDate: false,
    priceRateEndDate: false,
    originCities: false,
    discount: false,
    versionName: false,
    archiveFile: false,
    retailBasedDistrict: false,
    versionBasic: false
  };

  get customPriceOption() {
    return [
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceRetail").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceRetailDescription").toString(),
        value: "retail"
      },
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceNormal").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceNormalDescription").toString(),
        value: "normal"
      },
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceSpecial").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceSpecialDescription").toString(),
        value: "khusus"
      },
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceCombination").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceCombinationDescription").toString(),
        value: "kombinasi"
      }
    ];
  }

  get canShowBtnAttachment(): boolean {
    return FlagsPermissionClient.permission_client_management_detail_custom_rates_view.isEnabled();
  }

  get canShowBtnDownloadNew(): boolean {
    return FlagsPermissionClient.permission_client_management_download_rate.isEnabled();
  }

  get isShowDownloadButton(): boolean {
    if (!this.canShowBtnDownloadNew) return false;

    const selectedStatus = [
      "active",
      "nearly_expired",
      "nearly-expired",
      "expired",
      "requested",
      "rejected",
      "for-sales",
      "for_sales",
      "partially"
    ].includes(this.clientStatus);

    const normalizedPriceType = this.priceType.toLowerCase();
    const isEligiblePriceType =
      ["custom_client", "custom_client_retail_district"].includes(
        normalizedPriceType
      ) ||
      [
        "combination",
        "combination_custom_3lc",
        "combination_retail_district",
        "combination_custom_3lc_retail_district"
      ].includes(normalizedPriceType) ||
      [
        "normal",
        "normal_custom_3lc",
        "normal_retail_district",
        "normal_custom_3lc_retail_district"
      ].includes(normalizedPriceType) ||
      normalizedPriceType.includes("legacy");

    return selectedStatus && isEligiblePriceType;
  }

  downloadExcel(fileDownload: string) {
    const link = document.createElement('a');
    link.href = fileDownload;
    link.download = ''; // biar browser tahu ini untuk diunduh
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  formatDate(date: string) {
    return date ? moment(date).format("DD MMMM YYYY") : "-";
  }

  formattedDate(date: string) {
    if (!date) return "-";

    const timezone =
      AccountController.accountData.account_type_detail.partnerLocation?.city
        ?.timezone;
    const utcOffset = timezone.replace("GMT+", "+0").replace(":", "");
    const tzName = configDates(timezone)["id"];

    const formatDate = moment(date)
      .utcOffset(utcOffset)
      .format("DD MMM YYYY, HH.mm");

    return `${formatDate} ${tzName}`;
  };

  get detailData() {
    if (this.isManagement) {
      return ClientManagementController.detailClientManagement;
    }

    return ClientRegistrationController.clientRegistrationDetail;
  }

  get clientStatusUpdatedAt() {
    return formatDate(this.detailData.clientStatusUpdatedAt);
  }

  get clientCurrentRate() {
    return this.detailData?.clientCurrentRate;
  }

  get clientStatus() {
    return (
      this.detailData?.clientCurrentRate?.clientStatusCustomRate.toLowerCase() ||
      ""
    );
  }

  get isPartially() {
    return (
      this.detailData.clientCurrentRate.clientStatusBulk === "partially" &&
      FlagsPermissionClient.partially_status_current_rate.isEnabled()
    );
  }

  get partiallyLabel() {
    return `Partially (${this.uploadDataProcesed})`;
  }

  get detailClientLiveRate() {
    return this.detailData?.clientLiveRate;
  }
  get isClientLiveRate() {
    return !!this.detailClientLiveRate.clientCustomRateId;
  }

  get clientLiveRateStatus() {
    return this.detailClientLiveRate?.clientStatusCustomRate.toLowerCase() || ""
  }

  get updatedByUsername() {
    return this.detailClientLiveRate?.clientStatusUpdatedBy;
  }
  get totalDataProcess() {
    return this.detailClientLiveRate?.totalDataProcessed;
  }
  get totalData() {
    return this.detailClientLiveRate?.totalData;
  }

  get customPriceRate() {
    if (this.detailClientLiveRate.clientRatePriceType === "custom_client" || this.detailClientLiveRate.clientRatePriceType === "custom_client_retail_district") {
      return this.$t("clientManagement.infoPriceClient.priceSpecial").toString();
    } else if (this.detailClientLiveRate.clientRatePriceType === "custom_client" || this.detailClientLiveRate.clientRatePriceType === "custom_client_retail_district") {
      return this.$t("clientManagement.infoPriceClient.priceCombination").toString();
    } else {
      return "-"
    }
  }

  get mapStatusRate() {
    let data = {
      classStatusRate: "",
      btnStatusRate: "",
      title: "",
      icon: "",
      iconColor: ""
    }
    if (this.clientLiveRateStatus === "uploading" || this.clientLiveRateStatus === "requested") {
      data = {
        classStatusRate: "text-red-lp-100 cursor-pointer",
        btnStatusRate: this.$t("clientManagement.infoPriceClient.viewPriceChange").toString(),
        title: this.clientLiveRateStatus === "requested"
          ? this.$t("clientManagement.infoPriceClient.waitingApprovalPriceChange").toString()
          : this.$t("clientManagement.infoPriceClient.uploadingPriceFile", { process: this.totalDataProcess, total: this.totalData }).toString(),
        icon: "clock-v4",
        iconColor: "#F09035"
      }
    } else if ((this.clientLiveRateStatus === "failed" && !this.updatedByUsername) || (this.clientLiveRateStatus === "failed" && this.updatedByUsername) || this.isRejectedOrCancelled) {
      data = {
        classStatusRate: "text-red-lp-500 bg-pink-lp-200",
        btnStatusRate: this.$t("clientManagement.infoPriceClient.failed").toString(),
        title:
          this.clientLiveRateStatus === "failed" && !this.updatedByUsername
            ?  this.$t("clientManagement.infoPriceClient.customRatesFailedBySystem").toString()
            : this.isRejected
            ? this.$t("clientManagement.infoPriceClient.customRatesNotApproved").toString()
            : this.isCancelled
            ? this.$t("clientManagement.infoPriceClient.customRatesCancelled").toString()
            : this.$t("clientManagement.infoPriceClient.priceFileFailedUpload").toString(),
        icon: "close-circle-fill",
        iconColor: ""
      }
    } else if (this.clientLiveRateStatus === "pending") {
      data = {
        classStatusRate: "text-gray-lp-300 bg-gray-lp-400",
        btnStatusRate: `Pending ${this.totalDataProcess}/${this.totalData}`,
        title: this.$t("clientManagement.infoPriceClient.priceChangeAccepted").toString(),
        icon: "check-green-fill",
        iconColor: ""
      }
    } else if (this.clientLiveRateStatus === "partially") {
      data = {
        classStatusRate: "text-yellow-lp-100 bg-yellow-lp-400",
        btnStatusRate: `Aktif ${this.totalDataProcess}/${this.totalData}`,
        title: this.$t("clientManagement.infoPriceClient.priceChangeAcceptedPartially").toString(),
        icon: "check-green-fill",
        iconColor: "#F09035"
      }
    } else if (this.clientLiveRateStatus === "active") {
      data = {
        classStatusRate: "text-green-lp-200 bg-green-lp-1200",
        btnStatusRate: this.$t("clientManagement.infoPriceClient.active").toString(),
        title: this.$t("clientManagement.infoPriceClient.priceChangeAccepted").toString(),
        icon: "check-green-fill",
        iconColor: ""
      }
    }

    return data;
  }

  changePrice() {
    if (this.clientLiveRateStatus === "uploading" || this.clientLiveRateStatus === "requested") {
      this.updatePrice = true;
    }
  }

  get periodeHarga() {
    if (this.isManagement) {
      const detailData = this.detailData as DetailClientManagement;
      return {
        start: formatDate(detailData.clientCurrentRate.clientRateversionStartDate, "DD MMM YYYY, HH:mm"),
        end: formatDate(detailData.clientCurrentRate.clientRateVersionEndDate, "DD MMM YYYY, HH:mm")
      }
    }

    return {
      start: formatDate((this.detailData as ClientRegistrationDetail).clientCurrentRate.clientRateVersionStartDate, "DD MMM YYYY, HH:mm"),
      end: formatDate((this.detailData as ClientRegistrationDetail).clientCurrentRate.clientRateVersionEndate, "DD MMM YYYY, HH:mm")
    };
  }

  get isErrorFile() {
    return (
      this.detailData.clientCurrentRate.clientErrorFileCustomRate &&
      this.detailData.clientCurrentRate.clientErrorNameCustomRate !==
        "error_log_.csv"
    );
  }

  get priceType() {
    return this.detailData?.clientCurrentRate.clientRatePriceType || "";
  }

  get priceTypeRenewRates() {
    if (
      this.detailData?.clientRenewalRate?.clientVersionBaseRate &&
      this.detailData?.clientRenewalRate?.clientVersionCustomRate
    ) {
      return "mix";
    }
    if (
      !this.detailData?.clientRenewalRate?.clientVersionBaseRate &&
      this.detailData?.clientRenewalRate?.clientVersionCustomRate
    ) {
      return "custom";
    }
    return "basic";
  }

  get tipeKostumisasiHarga() {
    if (this.priceType.toLowerCase() === "retail") {
      return this.$t("clientManagement.infoPriceClient.priceRetail").toString();
    } else if (
      this.priceType.toLowerCase() === "normal" ||
      this.priceType.toLowerCase() === "normal_custom_3lc" ||
      this.priceType.toLowerCase() === "normal_retail_district" ||
      this.priceType.toLowerCase() === "normal_custom_3lc_retail_district"
    ) {
      return this.$t("clientManagement.infoPriceClient.priceNormal").toString();
    } else if (
      this.priceType.toLowerCase() === "combination" ||
      this.priceType.toLowerCase() === "combination_custom_3lc" ||
      this.priceType.toLowerCase() === "combination_retail_district" ||
      this.priceType.toLowerCase() === "combination_custom_3lc_retail_district"
    ) {
      return this.$t("clientManagement.infoPriceClient.priceCombination").toString();
    } else if (
      this.priceType.toLowerCase() === "custom_client" ||
      this.priceType.toLowerCase() === "custom_client_retail_district"
    ) {
      return this.$t("clientManagement.infoPriceClient.priceSpecial").toString();
    } else if (this.priceType.toLowerCase().includes("legacy")) {
      return this.priceTypeRenewRates;
    } else {
      return this.$t("clientManagement.infoPriceClient.priceRetail").toString();
    }
  }

  get hargaKhusus() {
    if (
      this.priceType.toLowerCase() === "custom_client" ||
      this.priceType.toLowerCase() === "custom_client_retail_district" ||
      this.priceType.toLowerCase() === "combination"
    ) {
      return this.detailData?.clientCurrentRate.clientVersionCustomRate || "-";
    }
    return "-";
  }

  get statusString() {
    switch (this.clientStatus.toLowerCase()) {
      case "waiting":
      case "requested":
        return this.$t("clientManagement.infoPriceClient.waiting").toString();
      case "rejected":
        return this.$t("clientManagement.infoPriceClient.rejected").toString();
      case "cancelled":
        return this.$t("clientManagement.infoPriceClient.cancelled").toString();
      case "active":
        return this.$t("clientManagement.infoPriceClient.active").toString();
      case "nearly-expired":
      case "nearly_expired":
        return this.$t("clientManagement.infoPriceClient.nearlyExpired").toString();
      case "expired":
        return this.$t("clientManagement.infoPriceClient.expired").toString();
      case "pending":
        return this.$t("clientManagement.infoPriceClient.pending").toString();
      case "uploading":
        return this.$t("clientManagement.infoPriceClient.uploadStatus", { progress: this.uploadDataProcesed }).toString();
      default:
        return "-";
    }
  }

  get statusBgColor() {
    switch (this.clientStatus.toLowerCase()) {
      case "waiting":
      case "requested":
      case "uploading":
        return "#FEFAE8";
      case "rejected":
      case "cancelled":
        return "#F8E9E9";
      case "active":
      case "pending":
      case "nearly-expired":
      case "nearly_expired":
      case "expired":
        return "#EBF8FF";
      default:
        return "#AEAEAE";
    }
  }

  get statusTextColor() {
    switch (this.clientStatus.toLowerCase()) {
      case "waiting":
      case "requested":
      case "uploading":
        return "#F09035";
      case "rejected":
      case "cancelled":
        return "#B82025";
      case "pending":
      case "active":
      case "nearly-expired":
      case "nearly_expired":
      case "expired":
        return "#006644";
      default:
        return "-";
    }
  }

  get bannerColor() {
    switch (this.clientStatus.toLowerCase()) {
      case "waiting":
      case "requested":
      case "active":
      case "uploading":
        return "#006644";
      case "nearly-expired":
      case "nearly_expired":
        return "#FAB713";
      case "rejected":
      case "expired":
      case "cancelled":
        return "#CA0000";
      default:
        return "#78787A";
    }
  }

  get showUbahHargaButton() {
    switch (this.clientStatus.toLowerCase()) {
      case "requested":
      case "waiting":
      case "rejected":
      case "cancelled":
      case "uploading":
        return true;
      default:
        return false;
    }
  }

  get showButtonChangeInsurance() {
    return (
      this.clientStatus.toLowerCase() === "requested" &&
      this.detailData.clientType === "Client Induk"
    );
  }

  get shouldShow3LCOrigin() {
    const isNormal = this.priceType.toLowerCase().includes("normal");
    const isCombination = this.priceType.toLowerCase().includes("combination");
    return isNormal || isCombination;
  }

  get perhitunganAsuransi() {
    const insuranceValuePercentage = this.detailData.clientCurrentRate.clientRateInsuranceValuePercentage
      .toString()
      .replace(".", ",");
    switch (
      this.detailData.clientCurrentRate.clientRateInsuranceType.toLowerCase()
    ) {
      case "":
        return this.$t("clientManagement.infoPriceClient.retailTiering").toString();
      case "bundling":
        return this.$t("clientManagement.infoPriceClient.bundlingNoInsuranceFee").toString();
      case "custom":
        return this.$t("clientManagement.infoPriceClient.customRatesPercent", { percentage: insuranceValuePercentage }).toString();
      default:
        return "-";
    }
  }

  elipsisText(text: string) {
    return text.length > 30
      ? `${text.substr(0, 30)}...`
      : text;
  }

  downloadAttachment() {
    const url = this.detailData?.clientCurrentRate.clientFileCustomRate || "";
    downloadFile(url, fileNameFromUrl(url), fileExtensionFromUrl(url));
  }

  // download file
  downloadCustomRateFile(): void {
    try {
      const currentRateData = this.isManagement
        ? ClientManagementController.detailClientManagement.clientCurrentRate
        : ClientRegistrationController.clientRegistrationDetail.clientCurrentRate;
      const url = currentRateData.clientFileCustomRate;
      const fileName = currentRateData.clientFileNameCustomRate;



      const cleanUrl = (url || "").split("?")[0];
      const cleanFileName = (fileName || "").split("?")[0];

      // Try to read filename from query param ?path=...
      const queryPathName = this.extractFileNameFromPathParam(url);

      // Determine file extension: prefer from cleanFileName if it has a dot, otherwise from query path, fallback to URL
      const fileExt = (cleanFileName && cleanFileName.includes("."))
        ? (cleanFileName.split(".").pop() || "").toLowerCase()
        : (queryPathName && queryPathName.includes("."))
          ? (queryPathName.split(".").pop() || "").toLowerCase()
          : this.extractFileExtension(url);

      // Build base name source: prefer provided cleanFileName when it contains a dot, otherwise use query path name, fallback to URL basename
      const baseNameSource = (cleanFileName && cleanFileName.includes("."))
        ? cleanFileName
        : (queryPathName || fileNameFromUrl(cleanUrl));

      // Build new file name without extension (only strip if a dot exists)
      const newFileName = baseNameSource.includes(".")
        ? baseNameSource.split(".").slice(0, -1).join(".")
        : baseNameSource;
      const extWithDot = fileExt ? `.${fileExt}` : "";



      downloadFile(url, newFileName, extWithDot, needsAuth(url)).catch((error: any) => {
        MainAppController.showErrorMessage(
            parsingErrorResponse(error, this.$t("clientManagement.infoPriceClient.downloadFailedError").toString())
        );
      });
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error as any, this.$t("clientManagement.infoPriceClient.downloadFailedError").toString())
      );
    }
  }

  async downloadAttachmentBackground(): Promise<void> {
    const clientId = Number(this.detailData.id || 0);
    try {
      MainAppController.showLoading();
      if (this.isManagement) {
        await ClientManagementController.downloadAttachment(clientId);
      } else {
        await ClientRegistrationController.downloadAttachment(clientId);
      }
      MainAppController.setSnackbarMessage("File akan dikirim via email");
      MainAppController.setSnackbarMessage(this.$t("clientManagement.infoPriceClient.downloadAttachmentEmail").toString());
      MainAppController.setSnackbarTimeout(3000);
      MainAppController.setSnackbar(true);
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, this.$t("clientManagement.infoPriceClient.downloadAttachmentFailed").toString())
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  buildClientRateErrorFileName(
    type: string,
    clientCode: string | undefined | null
  ): string {
    return `${this.$t("clientManagement.infoPriceClient.downloadAttachmentFailedError").toString()} - ${type} - ${clientCode}${CLIENT_RATE_ERROR_FILE_EXT}`;
  }

  resolveClientPriceTypeLabel(priceType: string | undefined | null): string {
    const label: Record<string, string> = {
      custom_client: this.$t('clientManagement.infoPriceClient.specialPrice').toString(),
      combination: this.$t('clientManagement.infoPriceClient.priceCombination').toString(),
    };
    if (priceType) {
      if (label[priceType]) {
        return label[priceType];
      } else {
        return priceType;
      }
    } else {
      return this.$t('clientManagement.infoPriceClient.priceUnknown').toString();
    }
  }

  downloadErrorFile(): void {
    const currentRateData = this.isManagement
      ? ClientManagementController.detailClientManagement.clientCurrentRate
      : ClientRegistrationController.clientRegistrationDetail.clientCurrentRate;

    const clientCode = this.isManagement
      ? ClientManagementController.detailClientManagement.clientCode
      : ClientRegistrationController.clientRegistrationDetail.clientCode;
    if (
      currentRateData.clientRatePriceType === "combination" ||
      currentRateData.clientRatePriceType === "custom_client"
    ) {
      const filename = this.buildClientRateErrorFileName(
        this.resolveClientPriceTypeLabel(currentRateData.clientRatePriceType),
        clientCode
      );
      const fileId = currentRateData.clientErrorFileCustomRate.split("/").pop();

      ClientController.getBulkErrorReport({
        id: Number(fileId),
        filename: filename
      });
    } else {
      this.downloadErrorFileBE(currentRateData);
    }
  }

  downloadErrorFileBE(currentRateData: any): void {
    try {
      const url = currentRateData.clientErrorFileCustomRate;
      const fileName = currentRateData.clientErrorNameCustomRate;

      const cleanUrl = (url || "").split("?")[0];
      const cleanFileName = (fileName || "").split("?")[0];

      // Try to read filename from query param ?path=...
      const queryPathName = this.extractFileNameFromPathParam(url);

      // Determine file extension: prefer from cleanFileName if it has a dot, otherwise from query path, fallback to URL
      const fileExt = (cleanFileName && cleanFileName.includes("."))
        ? (cleanFileName.split(".").pop() || "").toLowerCase()
        : (queryPathName && queryPathName.includes("."))
          ? (queryPathName.split(".").pop() || "").toLowerCase()
          : this.extractFileExtension(url);

      // Build base name source: prefer provided cleanFileName when it contains a dot, otherwise use query path name, fallback to URL basename
      const baseNameSource = (cleanFileName && cleanFileName.includes("."))
        ? cleanFileName
        : (queryPathName || fileNameFromUrl(cleanUrl));

      // Build new file name without extension (only strip if a dot exists)
      const newFileName = baseNameSource.includes(".")
        ? baseNameSource.split(".").slice(0, -1).join(".")
        : baseNameSource;
      const extWithDot = fileExt ? `.${fileExt}` : "";

      downloadFile(url, newFileName, extWithDot, needsAuth(url)).catch((error: any) => {
        MainAppController.showErrorMessage(
            parsingErrorResponse(error, this.$t("clientManagement.infoPriceClient.downloadFailedError").toString())
        );
      });
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error as any, this.$t("clientManagement.infoPriceClient.downloadFailedError").toString())
      );
    }
  }

  // Fallback: extract file extension from URL
  extractFileExtension(url: string): string {
    try {
      if (!url) return "";
      const [baseUrl, queryString] = url.split("?");
      if (queryString) {
        const params = new URLSearchParams(queryString);
        const path = params.get("path");
        if (path) {
          const ext = path.split(".").pop() || "";
          return ext.toLowerCase();
        }
      }
      const ext = (baseUrl || url).split(".").pop() || "";
      return ext.toLowerCase();
    } catch {
      const ext = (url || "").split(".").pop() || "";
      return ext.toLowerCase();
    }
  }

  // Extract base file name (with extension) from "path" query param if present
  extractFileNameFromPathParam(url: string): string {
    try {
      if (!url) return "";
      const [, queryString] = url.split("?");
      if (!queryString) return "";
      const params = new URLSearchParams(queryString);
      const path = params.get("path") || "";
      const base = path.split("/").pop() || path;
      return base;
    } catch {
      return "";
    }
  }

  showAll() {
    this.modelDialog = true;
  }

  updateRate() {
    this.isUpdateRateConfirmation = true;
  }

  confirmUpdateRateConfirmation() {
    this.isUpdateRateConfirmation = false;
    this.updateRateDialog = true;
  }

  closeUpdateRateConfirmation() {
    this.isUpdateRateConfirmation = false;
  }

  changeInsurance() {
    this.isUpdateInsurance = true;
  }

  closeChangeInsurance() {
    this.isUpdateInsurance = false;
  }

  getDetailClientRegistrationV2() {
    ClientRegistrationController.getDetailV2(this.detailData.id);
  }

  formateRatePeriod(start: string, end: string) {
    const isSameMonthYear =
      formatDateNormal(start, "MMMM-YYYY") ===
      formatDateNormal(end, "MMMM-YYYY");

    return `${formatDateNormal(
      start,
      isSameMonthYear ? "DD" : "DD MMMM YYYY"
    )} - ${formatDateNormal(end, "DD MMMM YYYY")}`;
  }

  // get Price Type
  get priceTypeVal() {
    let priceTypeVal = "";
    const lowerCustomRate = this.form.isCustomRate.toLowerCase();
    if (lowerCustomRate === "retail") priceTypeVal = "retail";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value === "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "normal";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value !== "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "normal_custom_3lc";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value === "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "normal_retail_district";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value !== "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "normal_custom_3lc_retail_district";
    if (lowerCustomRate === "khusus" && !this.form.retailBasedDistrict)
      priceTypeVal = "custom_client";
    if (lowerCustomRate === "khusus" && this.form.retailBasedDistrict)
      priceTypeVal = "custom_client_retail_district";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value === "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "combination";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value !== "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "combination_custom_3lc";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value === "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "combination_retail_district";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value !== "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "combination_custom_3lc_retail_district";
    return priceTypeVal;
  }

  isDiscountValid() {
    if (!this.form.discount || !Array.isArray(this.form.discount)) {
      return false;
    }
    return this.form.discount.every((item: any) => item.discount && String(item.discount).trim() !== "");
  }

  get isValidUpdateRate() {
    let result = false;
    const isValidDiscount = this.form.useClientDiscountConfig
      ? this.isDiscountValid() && !this.error.discount
      : true;
    if (this.form.isCustomRate === "retail") {
      if (
        !this.form.isInsurance ||
        (this.form.isInsurance &&
          this.form.insurance &&
          !this.error.customRates)
      )
        result = true;
    }
    if (this.form.isCustomRate === "normal") {
      if (
        (this.form.versionBasic &&
          this.form.originCities.length &&
          isValidDiscount &&
          !this.form.isInsurance) ||
        (this.form.versionBasic &&
          this.form.originCities.length &&
          isValidDiscount &&
          this.form.isInsurance &&
          this.form.insurance &&
          !this.error.customRates)
      )
        result = true;
    }
    if (this.form.isCustomRate === "khusus") {
      if (
        (this.form.versionName &&
          this.form.archiveFile.name &&
          !this.form.isInsurance) ||
        (this.form.versionName &&
          this.form.archiveFile.name &&
          this.form.isInsurance &&
          this.form.insurance &&
          !this.error.customRates)
      )
        result = true;
    }
    if (this.form.isCustomRate === "kombinasi") {
      if (
        (this.form.versionName &&
          this.form.versionBasic &&
          this.form.originCities.length &&
          isValidDiscount &&
          this.form.archiveFile.name &&
          !this.form.isInsurance) ||
        (this.form.versionName &&
          this.form.versionBasic &&
          this.form.originCities.length &&
          isValidDiscount &&
          this.form.archiveFile.name &&
          this.form.isInsurance &&
          this.form.insurance &&
          !this.error.customRates)
      )
        result = true;
    }
    return (
      result && !this.error.priceRateStartDate && !this.error.priceRateEndDate
    );
  }

  get isBasicRateBool() {
    return this.form.isCustomRate === "normal";
  }
  get isMixRateBool() {
    return this.form.isCustomRate === "kombinasi";
  }
  get isCustomRateBool() {
    return this.form.isCustomRate === "khusus";
  }

  get priceStartDate() {
    const startDate = new Date(this.detailData.clientBilling.startDate);
    startDate.setDate(startDate.getDate() + 1);
    return startDate;
  }

  async submitUpdateRate() {
    const detailData = this.detailData as ClientRegistrationDetail;
    const renewalRate: any = this.form;
    const { discount } = this.form;
    const findValueSemua = Array.isArray(discount) ? discount.find((item: any) => item.product.name === "SEMUA") : null;
    const mapDiscountProduct =
    findValueSemua
      ? null
      : Array.isArray(discount) ? discount.filter((d: any) => d.product.name).map((item: any) => {
          return {
            product_type: item.product.name,
            discount: typeof item.discount === "string" ? Number(item.discount.replace(/,/g, ".")) : Number(item.discount)
      }
    }) : null
    const discountPrice = findValueSemua ? (typeof discount[0].discount === "string" ? discount[0].discount.replace(/,/g, ".") : String(discount[0].discount)) : null;
    const payload = new EditClientRegistrationApiRequestV2({
      id: this.detailData.id,
      companyName: this.detailData.clientName,
      email: this.detailData.clientEmail,
      phone: `62${this.detailData.clientPhone}`,
      address: this.detailData.clientAddress,
      districtCode: this.detailData.clientDistrict?.code,
      taxNumber: this.detailData.clientTaxNumber,
      versionBasic:
        typeof this.form.versionBasic === "object"
          ? this.form.versionBasic.rateVersionName
          : this.form.versionBasic,
      isCustomRate: this.isMixRateBool,
      discount: discountPrice,
      discountProductTypes: mapDiscountProduct ?? [],
      priceRateStartDate: this.$moment(
        this.form.isCustomRate === "retail"
          ? this.priceStartDate
          : this.form.priceRateStartDate
      ).format("YYYY-MM-DDTHH:mm:ssZ"),
      priceRateEndDate: this.$moment(
        this.form.isCustomRate === "retail"
          ? this.detailData.clientBilling.endDate
          : this.form.priceRateEndDate
      ).format("YYYY-MM-DDTHH:mm:ssZ"),
      versionName: renewalRate.versionName,
      archiveFile:
        typeof renewalRate.archiveFile === "object"
          ? renewalRate.archiveFile
          : "",
      isUpdateCustom:
        typeof renewalRate.archiveFile === "object" ? true : false,
      clientType:
        this.detailData.clientType === "Client Induk" ? "parent" : "branch",
      category: this.detailData.clientCategory,
      bankName: detailData.clientBankLabel,
      bankAccountName: detailData.clientBankName,
      bankAccountNumber: detailData.clientBankNumber,
      bankAccountEmail: detailData.clientBankEmail,
      bankAccountLabel: detailData.clientBankLabel,
      contactName: detailData.clientContact.clientContactName,
      contactJobTitle: detailData.clientContact.clientContactJobTitle,
      contactEmail: detailData.clientContact.clientContactEmail,
      contactPhone: `62${detailData.clientContact.clientContactPhone}`,
      posId: detailData.clientPartner.clientPartnerId,
      posCommission: detailData.clientPartner.clientPartnerCommissionAmount,
      posQuantifier: detailData.clientPartner.clientPartnerQuantifierCommission,
      isDo: this.detailData.clientIsDo,
      isCod: this.detailData.clientIsCod,
      clientBranchCodReturnReceiverName: this.detailData.clientCodReject
        .clientCodName,
      clientBranchCodReturnReceiverPhone: this.detailData.clientCodReject
        .clientCodPhone,
      clientBranchCodReturnReceiverAddress: this.detailData.clientCodReject
        .clientCodAddress,
      clientBranchCodReturnReceiverCityCode: this.detailData.clientCodReject
        .clientCodDistrict.cityCode,
      clientBranchCodReturnReceiverDistrictCode: this.detailData.clientCodReject
        .clientCodDistrict.code,
      clientBranchCodFee: Number(
        this.detailData.clientCodReject.clientCodCommission
      ),
      isCustomRateOnly: this.isCustomRateBool,
      billingEmail: this.detailData.clientBilling.email.join(","),
      billingPaymentPeriod: this.detailData.clientBilling.timeRangePayment,
      priceType: this.priceTypeVal,
      taxpayerAddress: this.detailData.clientTax.taxPayerAddress,
      taxpayerName: this.detailData.clientTax.taxPayerAddress,
      billingScheduleSo: this.detailData.clientBilling.billingSchedule,
      billingWaNumber: this.detailData.clientBilling.noWhatsApp,
      companySize: this.detailData.clientCompanySize,
      contractEndDate: formatDateNormal(
        this.detailData.clientBilling.endDate,
        "YYYY-MM-DD"
      ),
      contractStartDate: formatDateNormal(
        this.detailData.clientBilling.startDate,
        "YYYY-MM-DD"
      ),
      insuranceType: this.form.isInsurance
        ? this.form.insurance === "custom-rates" || this.form.insurance === "custom"
          ? "custom"
          : "bundling"
        : "",
      letterCodeOrigin: this.form.originCities.length
        ? this.form.originCities
            .filter(
              (opt: OptionsClass) =>
                opt.name !== this.$t("clientManagement.priceTypeForm.all3lc").toString()
            )
            .map((opt: OptionsClass) => opt.name)
            .join(",")
        : "",
      vaBankAccountName: this.detailData.bankSettlement.accountName,
      vaBankEmail: this.detailData.bankSettlement.email,
      vaBankLabel: this.detailData.bankSettlement.bankLabel,
      vaBankName: this.detailData.bankSettlement.bankName,
      vaBankNumber: this.detailData.bankSettlement.accountNo,
      insurancePercentage: this.form.isInsurance ? this.insurancePercentage : "",
      useClientDiscountConfig: this.form.useClientDiscountConfig
    });

    MainAppController.showLoading();
    await ClientRegistrationController.editClientRegistrationV2(payload)
      .then(() => {
        this.updateRateDialog = false;
        MainAppController.setSnackbarMessage(
          this.$t("clientManagement.infoPriceClient.parentClientPriceChangeSuccess", { clientName: this.detailData.clientName }).toString()
        );
        MainAppController.setSnackbarTimeout(2000);
        MainAppController.setSnackbar(true);
        ClientRegistrationController.getDetailV2(this.detailData.id);
        this.onResetForm();
      })
      .catch(err => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            err,
            this.$t("clientManagement.infoPriceClient.parentClientPriceChangeFailed", { clientName: this.detailData.clientName }).toString()
          )
        );
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  get insurancePercentage() {
    return this.form.isInsurance && this.form.insurance === "custom-rates"
      ? Number(Number("0." + this.form.customRates).toFixed(2))
      : 0;
  }

  get lcOptions() {
    return this.detailData.clientCurrentRate.clientRateVersionCustom3lc
      ? this.detailData.clientCurrentRate.clientRateVersionCustom3lc.split(",")
      : [];
  }

  get rejectReasonMessage() {
    return (
      this.detailData.clientStatusUpdatedReason ||
      this.detailData.clientCurrentRate.clientStatusUpdatedReason
    );
  }

  get allDiscount() {
    return this.dataDiscount?.find((d: ClientDiscount) => d.productType === "SEMUA") || null;
  }

  get allDiscountValue() {
    return this.allDiscount?.discount || "0";
  }

  get isPriceTypeCustom() {
    return (
      this.priceType.toLowerCase() === "custom_client" ||
      this.priceType.toLowerCase() === "custom_client_retail_district"
    );
  }

  get uploadDataProcesed() {
    return `${this.detailData.dataProcessedCurrent}/${this.detailData.totalDataCurrent}`;
  }

  onResetForm() {
    this.form = {
      archiveFile: "",
      customRates: "",
      discount: "",
      discountProductTypes: "",
      insurance: "",
      isCustomRate: "retail",
      isInsurance: false,
      originCities: [],
      priceRateEndDate: "",
      priceRateStartDate: "",
      retailBasedDistrict: false,
      versionBasic: "",
      versionName: "",
      useClientDiscountConfig: true
    };
  }

  get isClientFreeze() {
    return this.detailData.clientFreeze;
  }

  get ratesRetryUploadConfig() {
    return FlagsPermissionClient.rates_retry_upload_enabled.isEnabled()
  }

  onOpenRetryConfirmationUploadRate() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("clientManagement.infoPriceClient.continueRetryProcess").toString(),
        message: this.$t("clientManagement.infoPriceClient.retryProcessMessage").toString(),
        onSubmit: this.retryUploadClientRate,
        onClose: MainAppController.closeMessageModal,
        textSuccess: this.$t("clientManagement.infoPriceClient.yesContinue").toString(),
        textCancel: this.$t("clientManagement.infoPriceClient.cancel").toString(),
        customWidth: "417px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-8",
      })
    );
  }

  get rateVersionUpdatedAt() {
    return formatDate(this.detailData.clientCurrentRate?.clientRateUpdatedAt);
  }

  get validationRetryButton() {
    if (!this.detailData.clientCurrentRate?.clientRateUpdatedAt) {
      return false
    }
    const now = new Date();
    const versionUpdateAt = new Date(this.detailData.clientCurrentRate?.clientRateUpdatedAt);
    const versionUpdateAtPlusOneHour = new Date(versionUpdateAt.getTime() + 60 * 60 * 1000);
    return now.getTime() < versionUpdateAtPlusOneHour.getTime();
  }

  async retryUploadClientRate() {
    MainAppController.closeMessageModal();
    const resp = await ClientController.retryUploadClientRate({
      clientId: this.detailData.id,
      rateVersionId: this.detailData.clientCurrentRate?.clientCustomRateId || 0
    });
    if (resp) {
      this.getDetailClientRegistrationV2()
    }
  }
  get isRequestedApproval() {
    return (
      this.detailData.clientLiveRate.clientStatusCustomRate.toLowerCase() ===
      "requested"
    );
  }
  get isParent() {
    return this.detailData.clientParentId === 0;
  }
  get id() {
    return this.$route.params.id;
  }
  async fetchDetail() {
    await ClientManagementController.getDetailV2(Number(this.id));

    if (this.detailData.clientParentId === 0) {
      BeneficiaryAccountController.getBeneficiaryAccList({
        page: 1,
        limit: 1,
        bankAccType: "beneficiary",
        actortype: "client",
        actorId: this.detailData.id
      });
    }
  }

  closeModalDiskon() {
    this.isVisibleModalDiskon = false;
  }
  openModalDsikon() {
    this.isVisibleModalDiskon = true;
  }

  get dataDiscount() {
    if (!this.detailData.clientCurrentRate?.discountProductTypes.length) {
      return [
        new ClientDiscount({
          productType: "SEMUA",
          discount: this.detailData.clientCurrentRate.clientDiscountBaseRate
        })
      ];
    }
    return this.detailData.clientCurrentRate?.discountProductTypes || [];
  }

  get isRejected() {
    return this.clientStatus.toLowerCase() == "rejected";
  }

  get isCancelled() {
    return this.clientStatus.toLowerCase() == "cancelled";
  }

  get isRejectedOrCancelled() {
    return this.isRejected || this.isCancelled;
  }

  get isCancelUploadRate() {
    return (
      FlagsPermissionClient.abort_upload_rate_enabled.isEnabled() &&
      this.clientStatus === "uploading" &&
      this.detailData.clientCurrentRate?.isStuck
    );
  }

  onConfirmCancelProcessUploadRate() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("clientManagement.infoPriceClient.cancelProcessMessage").toString(),
        message: this.$t("clientManagement.infoPriceClient.cancelProcessMessageConfirm").toString(),
        onSubmit: this.onCancelProcessUploadRate,
        onClose: MainAppController.closeMessageModal,
        textSuccess: this.$t("clientManagement.infoPriceClient.yesCancel").toString(),
        textCancel: this.$t("clientManagement.infoPriceClient.cancel").toString(),
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-8",
      })
    );
  }

  async onCancelProcessUploadRate() {
    try {
      MainAppController.closeMessageModal();
      MainAppController.showLoading();
      await ClientRegistrationController.abortUploadRate(
        new AbortUploadRateApiRequest({
          clientCustomRateId: this.detailData.clientCurrentRate
            .clientCustomRateId
        })
      );
      this.$emit("cancel-upload-rate");
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, this.$t("clientManagement.infoPriceClient.cancelProcessFailed").toString())
      );
    } finally {
      MainAppController.closeLoading();
    }
  }
}

export default InfoDetailClient;
